/**
 * Free Gift Bar — persistent cart-aware updater.
 *
 * Loaded once via theme.liquid (when settings.enable_free_gift_bar is true).
 * Survives cart drawer section re-renders by using a MutationObserver to
 * re-attach to any newly injected #free-gift-bar-wrapper nodes.
 *
 * Listens to:
 *  - on:cart:add        (new variant added to cart via product-form.js)
 *  - on:line-item:change (quantity change in cart via cart-items.js or product-form.js)
 *  - fetch intercept     (catches /cart/add, /cart/change, /cart/update calls from
 *                         third-party apps, upsell widgets, etc.)
 */
(function () {
  'use strict';

  // Guard: only initialise once per page load.
  if (window.__freeGiftBarInitialised) return;
  window.__freeGiftBarInitialised = true;

  // Cached cart state — used to instantly repaint after section re-renders
  // so the banner never shows stale server-rendered values.
  var cachedCart = null;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Reads configuration from the .free-gift-banner data attributes.
   * Returns null when no banner element exists in the DOM yet.
   * @returns {object|null}
   */
  function readConfig() {
    const banner = document.querySelector('.free-gift-banner');
    if (!banner) return null;

    const goalsStr = banner.dataset.freeGiftGoals || '';
    const goals = goalsStr.split(',').map((n) => parseInt(n, 10) || 0);
    const stepCount = parseInt(banner.dataset.stepCount || '0', 10);

    return {
      stepCount,
      goal1: goals[0] || 0,
      goal2: goals[1] || 0,
      goal3: goals[2] || 0,
      label1: banner.dataset.label1 || '',
      label2: banner.dataset.label2 || '',
      label3: banner.dataset.label3 || '',
      i18nAllGoals: banner.dataset.i18nAllGoals || '',
      i18nDeficitTemplate: banner.dataset.i18nDeficit || ''
    };
  }

  /**
   * Formats a deficit amount and label into the i18n template string.
   * The Liquid t filter embeds ##AMOUNT## and ##LABEL## as placeholders.
   * @param {string} template
   * @param {number} amountCents
   * @param {string} label
   * @returns {string}
   */
  function buildDeficitText(template, amountCents, label) {
    const amountFormatted = Math.round(amountCents / 100) + ' kr';
    return template
      .replace('##AMOUNT##', amountFormatted)
      .replace('##LABEL##', label);
  }

  // ---------------------------------------------------------------------------
  // DOM update
  // ---------------------------------------------------------------------------

  /**
   * Updates every .free-gift-banner on the page with the latest cart data.
   * Called after any cart mutation.
   * @param {number} totalPrice  — cart.total_price in cents
   * @param {number} itemCount   — cart.item_count
   */
  function updateAllBanners(totalPrice, itemCount) {
    const wrappers = document.querySelectorAll('#free-gift-bar-wrapper');
    if (!wrappers.length) return;

    // Toggle visibility when cart empties
    wrappers.forEach((wrapper) => {
      wrapper.hidden = itemCount === 0;
    });

    if (itemCount === 0) return;

    const cfg = readConfig();
    if (!cfg || cfg.stepCount === 0) return;

    const { stepCount, goal1, goal2, goal3, label1, label2, label3,
      i18nAllGoals, i18nDeficitTemplate } = cfg;

    const cartTotal = totalPrice;
    let currentStep = 0;
    let progress = 0;
    let nextGoalText = '';

    if (stepCount === 3) {
      if (cartTotal >= goal3) {
        currentStep = 3; progress = 100;
      } else if (cartTotal >= goal2) {
        currentStep = 2;
        const stepRange = goal3 - goal2;
        progress = 66.66 + (stepRange ? ((cartTotal - goal2) / stepRange) * 33.33 : 0);
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal3 - cartTotal, label3);
      } else if (cartTotal >= goal1) {
        currentStep = 1;
        const stepRange = goal2 - goal1;
        progress = 33.33 + (stepRange ? ((cartTotal - goal1) / stepRange) * 33.33 : 0);
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal2 - cartTotal, label2);
      } else {
        progress = goal1 ? (cartTotal / goal1) * 33.33 : 0;
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal1 - cartTotal, label1);
      }
    } else if (stepCount === 2) {
      if (cartTotal >= goal2) {
        currentStep = 2; progress = 100;
      } else if (cartTotal >= goal1) {
        currentStep = 1;
        const stepRange = goal2 - goal1;
        progress = 50 + (stepRange ? ((cartTotal - goal1) / stepRange) * 50 : 0);
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal2 - cartTotal, label2);
      } else {
        progress = goal1 ? (cartTotal / goal1) * 50 : 0;
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal1 - cartTotal, label1);
      }
    } else if (stepCount === 1) {
      if (cartTotal >= goal1) {
        currentStep = 1; progress = 100;
      } else {
        progress = goal1 ? (cartTotal / goal1) * 100 : 0;
        nextGoalText = buildDeficitText(i18nDeficitTemplate, goal1 - cartTotal, label1);
      }
    }

    progress = Math.min(Math.max(progress, 0), 100);

    // Apply to every banner instance (cart page + cart drawer may both be present)
    document.querySelectorAll('.free-gift-banner').forEach((banner) => {
      const textEl = banner.querySelector('.free-gift-banner__text');
      const barEl = banner.querySelector('.free-gift-banner__progress-bar');
      const stepEls = banner.querySelectorAll('.free-gift-banner__step');

      if (!textEl || !barEl) return;

      textEl.innerHTML = currentStep >= stepCount ? i18nAllGoals : nextGoalText;
      barEl.style.width = progress.toFixed(1) + '%';

      stepEls.forEach((step, i) => {
        step.classList.toggle('completed', i < currentStep);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Cart fetch helper
  // ---------------------------------------------------------------------------

  /**
   * Fetches /cart.js and calls updateAllBanners with the result.
   * Used as a fallback when a sections-based response doesn't carry total_price.
   */
  function fetchCartAndUpdate() {
    fetch('/cart.js')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        cachedCart = { totalPrice: data.total_price, itemCount: data.item_count };
        updateAllBanners(data.total_price, data.item_count);
      })
      .catch((err) => console.error('[free-gift-bar] cart fetch error:', err));
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  /**
   * Handles on:cart:add — fires when a new variant is added to the cart.
   * event.detail.cart is the full cart object returned by /cart.js.
   */
  document.addEventListener('on:cart:add', function (evt) {
    if (evt.detail && evt.detail.cart) {
      const { cart } = evt.detail;
      if (typeof cart.total_price === 'number') {
        cachedCart = { totalPrice: cart.total_price, itemCount: cart.item_count };
        updateAllBanners(cart.total_price, cart.item_count);
      } else {
        fetchCartAndUpdate();
      }
    } else {
      fetchCartAndUpdate();
    }
  });

  /**
   * Handles on:line-item:change — fires when a cart item quantity changes
   * (including when an existing variant is added again, or removed).
   */
  document.addEventListener('on:line-item:change', function (evt) {
    if (evt.detail && evt.detail.cart) {
      const { cart } = evt.detail;
      if (typeof cart.total_price === 'number') {
        cachedCart = { totalPrice: cart.total_price, itemCount: cart.item_count };
        updateAllBanners(cart.total_price, cart.item_count);
      } else {
        fetchCartAndUpdate();
      }
    } else {
      fetchCartAndUpdate();
    }
  });

  // ---------------------------------------------------------------------------
  // Fetch intercept — catches third-party / upsell cart mutations
  // ---------------------------------------------------------------------------

  /**
   * Returns true if the URL points to a cart mutation endpoint.
   * Handles absolute and relative URLs and ignores query strings.
   * @param {string|Request} input
   * @returns {boolean}
   */
  function isCartMutationUrl(input) {
    const url = (typeof input === 'string' ? input : (input && input.url) || '').split('?')[0];
    return url.includes('/cart/change') || url.includes('/cart/add') || url.includes('/cart/update');
  }

  const _originalFetch = window.fetch;
  window.fetch = async function (input, init) {
    const response = await _originalFetch(input, init);

    if (isCartMutationUrl(input)) {
      response.clone().json().then((data) => {
        if (data && typeof data.total_price === 'number') {
          // Plain cart object (no sections rendering)
          cachedCart = { totalPrice: data.total_price, itemCount: data.item_count };
          updateAllBanners(data.total_price, data.item_count);
        } else {
          // Sections-rendered response — fall back to explicit cart fetch.
          // Small delay to ensure the cart drawer has finished re-rendering
          // its section HTML before we re-query the DOM for banner elements.
          setTimeout(fetchCartAndUpdate, 100);
        }
      }).catch(() => {
        setTimeout(fetchCartAndUpdate, 100);
      });
    }

    return response;
  };

  // ---------------------------------------------------------------------------
  // MutationObserver — re-attach after section re-renders
  // ---------------------------------------------------------------------------

  /**
   * When the cart drawer re-renders its section HTML (via renderContents),
   * a fresh #free-gift-bar-wrapper is injected into the DOM. The server-rendered
   * values inside it are stale (based on cart state at page load). We detect this
   * via MutationObserver and immediately re-fetch + update to get current values.
   */
  if ('MutationObserver' in window) {
    const observer = new MutationObserver(function (mutations) {
      let sawNewWrapper = false;

      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          // Check if the added node is (or contains) a gift bar wrapper
          if (
            node.id === 'free-gift-bar-wrapper' ||
            node.querySelector('#free-gift-bar-wrapper')
          ) {
            sawNewWrapper = true;
          }
        });
      });

      if (sawNewWrapper) {
        // Instantly repaint the newly injected banner with cached cart data
        // to avoid a flash of stale server-rendered values.
        if (cachedCart) {
          updateAllBanners(cachedCart.totalPrice, cachedCart.itemCount);
        } else {
          fetchCartAndUpdate();
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Initial sync on page load
  // ---------------------------------------------------------------------------

  // Ensure the banner reflects the actual cart state even if the page was served
  // from cache with a stale cart (e.g. bfcache restore or CDN-cached page).
  document.addEventListener('DOMContentLoaded', function () {
    fetchCartAndUpdate();
  });

  // Also handle bfcache restores (back/forward navigation)
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) fetchCartAndUpdate();
  });
}());
